import { ChangeEvent, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { t } from 'i18next';
import {
  ButtonType,
  Card,
  HeaderStyle,
  //IconButton,
  IconButtonProps,
  IconStyle,
  Modal,
  TableContainer,
  Theme,
} from '../../ui/components';
import { formatFullDate } from '../../ui/utils/dateUtils';
import { LoggerService, processProfilePictureData, getAccessToken } from '../../ui/utils';
import userService from '../../sdk/services/userService';
import { customEventPropType } from '../../ui/components/ImageEditor/types/propsType';
import ProgressBar from '../../ui/components/ProgressBar/ProgressBar';
import ImageEditor from '../../ui/components/ImageEditor/ImageEditor';
import personalizeImg from '../../assets/image/ProfileImageUpload.svg';
import type { AppTokenModel, DynamicObjectInterface } from '../../sdk/type';
import ChangeButton from '../../../src/assets/image/icons/change_button.svg';
import RemoveButton from '../../../src/assets/image/icons/remove_button.svg';
import { useAPICall } from '../../hooks';
import AttachmentService from '../../sdk/services/attachmentService';
import '../../sdk/services/axiosInterceptor';
import ParticipantService from '../../sdk/services/participantService';
import ShareDrawer from '../../ui/components/DrawerAlt/ShareDrawer';
import { useAdminToast } from '../../ui/components/AdminToast/AdminToast';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import styles from './Profile.module.scss';
import { getParticipationHistoryConfig } from './profileConfig';

export interface UserProfileData {
  name: string;
  email: string;
  phone: string;
  gender: string;
  race: string;
  occupation: string;
  incomeRange: string;
  address: string;
  state: string;
  createdDateTime: string;

  loginId: string;
  hightestPinLevel: number;
  pinLevel: number;
  typeOfUser: string;
  uplineDD: string;
  ddName: string;
  uplineDI: string;
  diName: string;
  loaName: string;
}

export const ProfileDetails = (props: { decodedData: DynamicObjectInterface }) => {
  const [urlParamValue, setUrlParamValue] = useState({
    campaignId: '',
    teamId: '',
    userId: '',
  });

  const access_token = getAccessToken() || '';
  let decoded: AppTokenModel | null = access_token ? jwtDecode(access_token) : null;

  const [data, setData] = useState<UserProfileData>();
  const [participationHistory, setParticipationHistory] = useState([]);

  //const [showConfirmation, setShowConfirmation] = useState(false);

  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [incompletePercentage, setIncompletePercentage] = useState<DynamicObjectInterface>('50');
  const [selectedData, setSelectedData] = useState({
    campaignId: '',
    teamId: '',
    code: '',
  });
  // const [showEdit, setShowEdit] = useState<SectionState>(
  //   Object.fromEntries(Object.values(Sections).map(section => [section, false])) as SectionState,
  // );

  const apiCall = useAPICall();
  const location = useLocation();
  const navigate = useNavigate();
  const { adminToast } = useAdminToast();

  useEffect(() => {
    setUrlParamValue(prev => {
      const params = new URLSearchParams(location.search);
      const userId = params.get('id') === null ? decoded?.userId?.toString() : (params.get('id') ?? '');
      prev.userId = userId ?? '';
      return { ...prev };
    });
    fetchData();
  }, [location]);

  const fetchData = async () => {
    if (urlParamValue.userId != null && urlParamValue.userId !== '') {
      const res = await apiCall(userService.getUserDetails, urlParamValue.userId);
      if (res.data.data) {
        let _data: UserProfileData = res.data.data;
        // _data.hideProfileImg = true;
        // _data.showFiveProgress = true;
        // _data.weightIn.weightInDate = _data.weightIn.weightedDate;
        // _data.weightOut.weightOutDate = _data.weightOut.weightedDate;
        // if (props.role === 'COACH') {
        //   _data.points = {
        //     product: (_data.point.purchase ?? 0) + (_data.point.purchaseAdj ?? 0),
        //     result: (_data.point.winning ?? 0) + (_data.point.winningAdj ?? 0),
        //     subscribe: (_data.point.subscribe ?? 0) + (_data.point.subscribeAdj ?? 0),
        //   };
        // }
        setData(_data);
        const randomPercentage = await incompletePercentageRange(_data); // Generates a random number between 0 and 100
        setIncompletePercentage(randomPercentage);
      } else {
        if (decoded == null) {
          navigate('/');
        }
      }
    } else {
      navigate('/');
    }
  };

  const incompletePercentageRange = (data: DynamicObjectInterface) => {
    let hasCompleted = 0;
    let totalMustComplete = 6;

    if (data.phone) {
      hasCompleted += 1;
    }
    if (data.gender) {
      hasCompleted += 1;
    }
    if (data.race) {
      hasCompleted += 1;
    }
    if (data.address) {
      hasCompleted += 1;
    }
    if (data.occupation) {
      hasCompleted += 1;
    }
    if (data.incomeRange) {
      hasCompleted += 1;
    }

    const calculatedData = Math.floor((hasCompleted / totalMustComplete) * 100);
    return calculatedData;
  };

  // const goBack = () => {
  //   let idx = location.pathname.indexOf('/home');
  //   if (idx !== -1) {
  //     let url = location.pathname.substring(0, idx);
  //     navigate(url);
  //   }
  // };

  const handleProfileFileImage = (e: customEventPropType, view: string) => {
    const file = e.files[0];
    setProfileImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setProfileImageUrl(objectUrl);
    setShowModal(false);
  };
  const handleProfileFile = (e: ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0] as File | null;

    setProfileImageFile(file);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setProfileImageUrl(objectUrl);
    } else {
      setProfileImageUrl('');
    }
    setShowModal(false);
  };

  const deleteProfile = async () => {
    const payload = {
      loginId: urlParamValue?.userId,
    };
    const deleteProfileResponse = await apiCall(AttachmentService.removeProfile, payload);

    if (deleteProfileResponse.status === 200) {
      setProfileImageUrl('');
    }
  };

  const fetchParticipationHistory = async () => {
    try {
      let participant = await apiCall(ParticipantService.getParticipantHistory);
      setParticipationHistory(participant?.data?.data);
    } catch (error) {
      LoggerService.log('Error fetchParticipationHistory', error);
    }
  };

  const fetchProfileImage = async () => {
    try {
      const response = await AttachmentService.downloadProfileImage(decoded?.userId.toString() ?? '');
      if (response.data && response.data.data && response.data.data.image) {
        setProfileImageUrl(response.data.data.image);
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };
  useEffect(() => {
    fetchProfileImage();
    fetchParticipationHistory();
  }, []);

  useEffect(() => {
    if (profileImageFile !== null) {
      handleProfileUpload();
    }
  }, [profileImageFile]);

  const handleProfileUpload = async () => {
    try {
      if (profileImageFile && decoded?.userId) {
        const formData = processProfilePictureData(profileImageFile, decoded?.userId.toString());
        const response = await apiCall(() => AttachmentService.postUploadProfile(formData));
        if (response.status === 200) {
          LoggerService.log('Successfully upload image', response);
        }
      }
    } catch (error) {
      LoggerService.log('Error upload image', error);
    }
  };

  // const onSave = async () => {
  //   let payload = {
  //     campaignId: urlParamValue?.campaignId,
  //     teamId: urlParamValue?.teamId,
  //     allocation: {
  //       alp: {
  //         invoices: [
  //           {
  //             recId: data?.subscribe[0]?.recId || null,
  //             orderPoint: 1,
  //             orderNumber: alpValue.input,
  //           },
  //         ],
  //         point: data?.point?.subscribe ?? 0,
  //         pointAdj: data?.point?.subscribeAdj ?? 0,
  //       },
  //     },
  //   };

  //   const result = await apiCall(
  //     ParticipantService.updateParticipantDetails,
  //     {
  //       ...payload,
  //       section: 'pointAllocation',
  //     },
  //     urlParamValue?.userId,
  //   );

  //   if (result.status === 200) {
  //     adminToast({
  //       message: t('admin.participant.updateSuccess'),
  //       theme: Theme.SUCCESS,
  //     });
  //     fetchData();
  //     setShowConfirmation(false);
  //   } else {
  //     adminToast({
  //       message: t('admin.participant.updateFail'),
  //       theme: Theme.ERROR,
  //     });
  //   }
  // };

  // const handleOnChange = (value: DynamicObjectInterface, key: 'input' | 'check') => {
  //   setAlpValue(prev => {
  //     return {
  //       ...prev,
  //       [key]: value,
  //     };
  //   });
  // };

  const showModalFn = () => {
    setShowModal(true);
  };

  const uploadImageModal = (
    <>
      {showModal && (
        <Modal
          primaryButtonLabel={''}
          headline={'Upload your Profile Picture'}
          containerWidth={'600px'}
          containerHeight={'600px'}
          closeIcon={true}
          onClose={() => setShowModal(false)}
        >
          <div>
            {profileImageUrl ? (
              <div className={styles['profile-image-container']}>
                <div className={styles['image-wrapper']}>
                  <img
                    src={profileImageUrl}
                    alt="Before Front View"
                    className={styles['profile-image']}
                    style={{ borderRadius: '50%' }}
                  />
                </div>
                <div className={styles['buttons-container']}>
                  <button
                    className={styles['change-photo']}
                    onClick={() => document.getElementById('profileImageUrl')?.click()}
                  >
                    <img src={ChangeButton} alt="bg" />
                    <input
                      type="file"
                      id="profileImageUrl"
                      accept="image/*"
                      onChange={e => handleProfileFile(e, 'profileImageUrl')}
                      style={{ display: 'none' }}
                    />
                  </button>
                  <button className={styles['remove-photo']} onClick={() => deleteProfile()}>
                    <img src={RemoveButton} alt="bg" />
                  </button>
                </div>
              </div>
            ) : (
              <ImageEditor onFileUpload={e => handleProfileFileImage(e, 'file')} accept="image/*" />
            )}
          </div>
        </Modal>
      )}
    </>
  );

  const shareCertificateModal = (
    <>
      {selectedData?.code && (
        <ShareDrawer
          open={!!selectedData?.code}
          onClose={() =>
            setSelectedData({
              campaignId: '',
              teamId: '',
              code: '',
            })
          }
          title="Share Via"
          programUrl={`${window.location.origin}/certificate?campaignId=${selectedData.campaignId}&teamId=${selectedData.teamId}&code=${selectedData.code}`}
          programTitle=""
        />
      )}
    </>
  );

  const getButtonConfig = (decoded: DynamicObjectInterface): IconButtonProps[] => {
    return [
      {
        iconName: 'pencil' as IconName,
        iconStyle: IconStyle.SOLID,
        theme: Theme.WHITE,
        title: t('admin.participant.edit'),
        onClick: () => navigate(`edit?id=${decoded?.userId}`),
      },
    ];
  };

  const onView = (payload: DynamicObjectInterface) => {
    window.open(`/certificate?campaignId=${payload.campaignId}&teamId=${payload.teamId}&code=${payload.code}`);
  };

  const onDownload = async (payload: DynamicObjectInterface) => {
    try {
      const response = await apiCall(ParticipantService.downloadCertificate, payload);

      if (response.status === 200 && response.data) {
        // Ensure data exists
        const data = response.data;
        const blob = new Blob([data], { type: 'image/svg' }); // Correct MIME type

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `certificate-${payload.code}.png`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release the object URL resources after the download is triggered
        URL.revokeObjectURL(link.href);
      } else {
        adminToast({
          message: response?.data?.data?.message || 'An unexpected error occurred while downloading the certificate.',
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      adminToast({
        message: 'Failed to download the certificate. Please try again.',
        theme: Theme.ERROR,
      });
    }
  };

  const onShare = (payload: DynamicObjectInterface) => {
    setSelectedData(payload);
  };

  return (
    <div className={styles['team-profile']}>
      {/* <div className={styles['team-profile-header']}>
        <IconButton iconName="chevron-left" iconStyle={IconStyle.SOLID} theme={Theme.WHITE} onClick={() => goBack()} />
      </div> */}
      <div>
        <div className={styles['profile-spacing-v']}>
          <Card>
            <div className={styles['profile-content']}>
              <div className={styles['profile-content-image-container']}>
                <img
                  src={profileImageUrl ? profileImageUrl : personalizeImg}
                  width={96}
                  height={96}
                  alt="personalize"
                  onClick={props.decodedData.name === data?.name ? showModalFn : undefined}
                  className={styles['main-profile-image']}
                />
              </div>
              <div className={styles['profile-content-image-display']}>
                <div className={styles['input']}>{data?.name ?? '-'}</div>
                <div className={styles['profile-info-item']}>
                  {/* <div className={styles['label']}>{t('admin.profile.profileCreated')}</div> */}
                  <div className={styles['input']}>
                    {t('admin.profile.profileCreated')}: {formatFullDate(data?.createdDateTime ?? '-')}
                  </div>
                </div>
              </div>
            </div>
          </Card>
          {incompletePercentage !== 100 && (
            <Card>
              <ProgressBar
                percentage={incompletePercentage}
                header="Incomplete Profile"
                footer={t('admin.profile.missingDetails')}
              />
            </Card>
          )}
          <Card
            title="Personal Information"
            headerStyle={HeaderStyle.HEADER_6}
            buttonType={ButtonType.ICON}
            button={getButtonConfig(decoded)}
          >
            <div className={styles['profile']}>
              <div className={styles['profile-info']}>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.name')}</div>
                  <div className={styles['input']}>{data?.name ?? '-'}</div>
                </div>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.aboId')}</div>
                  <div className={styles['input']}>{data?.loginId ?? '-'}</div>
                </div>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.contactNo')}</div>
                  <div className={styles['input']}>{data?.phone ?? '-'}</div>
                </div>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.email')}</div>
                  <div className={styles['input']}>{data?.email ?? '-'}</div>
                </div>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.gender')}</div>
                  <div className={styles['input']}>{data?.gender ?? '-'}</div>
                </div>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.race')}</div>
                  <div className={styles['input']}>{data?.race ?? '-'}</div>
                </div>
              </div>
              <div className={styles['profile-info']}>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.address')}</div>
                  <div className={styles['input']}>{data?.address ?? '-'}</div>
                </div>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.occupation')}</div>
                  <div className={styles['input']}>{data?.occupation ?? '-'}</div>
                </div>
                <div className={styles['profile-info-item']}>
                  <div className={styles['label']}>{t('admin.profile.incomeRange')}</div>
                  <div className={styles['input']}>{data?.incomeRange ?? '-'}</div>
                </div>

                {/* <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.state')}</div>
                    <div className={styles['input']}>{data?.state ?? '-'}</div>
                  </div> */}
              </div>
            </div>
          </Card>
          {/* <Card title="Business Information" headerStyle={HeaderStyle.HEADER_6}>
              <div className={styles['profile']}>
                <div className={styles['profile-info']}>
                  <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.aboId')}</div>
                    <div className={styles['input']}>{data?.loginId ?? '-'}</div>
                  </div>
                  <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.hightestPinLevel')}</div>
                    <div className={styles['input']}>{data?.hightestPinLevel ?? '-'}</div>
                  </div>
                  <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.typeOfUser')}</div>
                    <div className={styles['input']}>{data?.typeOfUser ?? '-'}</div>
                  </div>
                  <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.uplineDD')}</div>
                    <div className={styles['input']}>{data?.uplineDD ?? '-'}</div>
                  </div>
                  <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.ddName')}</div>
                    <div className={styles['input']}>{data?.ddName ?? '-'}</div>
                  </div>
                  <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.uplineDI')}</div>
                    <div className={styles['input']}>{data?.uplineDI ?? '-'}</div>
                  </div>
                  <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.diName')}</div>
                    <div className={styles['input']}>{data?.diName ?? '-'}</div>
                  </div>
                  <div className={styles['profile-info-item']}>
                    <div className={styles['label']}>{t('admin.profile.baloaName')}</div>
                    <div className={styles['input']}>{data?.loaName ?? '-'}</div>
                  </div>
                </div>
              </div>
            </Card> */}
        </div>
      </div>
      <Card title="Participation History" headerStyle={HeaderStyle.HEADER_6}>
        {participationHistory?.length > 0 ? (
          <TableContainer
            dataSource={participationHistory}
            getColumns={getParticipationHistoryConfig}
            rowKey="recId"
            cb={{ onView, onDownload, onShare }}
            displayOverflow={true}
            pagination={false}
          />
        ) : (
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        )}
      </Card>
      {/* {confirmationPrompt} */}
      {uploadImageModal}
      {shareCertificateModal}
      {/* {showEditModal} */}
    </div>
  );
};
